import React from 'react';

export interface MediaItem {
  id: string;
  url: string;
  width?: number;
  height?: number;
  type: 'image' | 'video';
  altText?: string;
}

export interface AdaptiveMasonryLayoutProps {
  items: MediaItem[];
  className?: string;
  onItemClick?: (item: MediaItem, index: number) => void;
  maxItems?: number;
}

interface Row {
  items: MediaItem[];
  totalWeight: number;
  aspectRatios: number[];
}

// Calculate image weight based on aspect ratio
function calculateImageWeight(width?: number, height?: number): number {
  if (!width || !height) return 1.0; // Default weight for unknown dimensions
  
  const aspectRatio = width / height;
  
  if (aspectRatio < 0.8) {
    // Portrait: tall and narrow
    return 0.5 + (aspectRatio * 0.25); // 0.5 - 0.7
  } else if (aspectRatio > 1.25) {
    // Landscape: wide and short  
    return 1.25 + Math.min(aspectRatio * 0.5, 0.75); // 1.25 - 2.0
  } else {
    // Square-ish: balanced
    return 1.0;
  }
}

// Get target weight per row based on container width
function getTargetWeight(containerWidth: number): number {
  if (containerWidth < 768) {
    // Mobile: 1-2 images per row
    return 1.5;
  } else if (containerWidth < 1024) {
    // Tablet: 2-3 images per row
    return 2.0;
  } else {
    // Desktop: 2-4 images per row
    return 2.75;
  }
}

// Calculate optimal rows using greedy algorithm
function calculateRows(items: MediaItem[], targetWeight: number): Row[] {
  const rows: Row[] = [];
  let currentRow: Row = { items: [], totalWeight: 0, aspectRatios: [] };
  
  for (const item of items) {
    const weight = calculateImageWeight(item.width, item.height);
    const aspectRatio = item.width && item.height ? item.width / item.height : 1;
    
    // Check if adding this item would exceed target weight
    if (currentRow.items.length > 0 && currentRow.totalWeight + weight > targetWeight) {
      // Finish current row and start new one
      rows.push(currentRow);
      currentRow = { items: [], totalWeight: 0, aspectRatios: [] };
    }
    
    // Add item to current row
    currentRow.items.push(item);
    currentRow.totalWeight += weight;
    currentRow.aspectRatios.push(aspectRatio);
  }
  
  // Add final row if it has items
  if (currentRow.items.length > 0) {
    rows.push(currentRow);
  }
  
  return rows;
}

// Calculate optimal height for a row
function calculateRowHeight(row: Row, containerWidth: number): number {
  const sumOfAspectRatios = row.aspectRatios.reduce((sum, ratio) => sum + ratio, 0);
  const gaps = (row.items.length - 1) * 4; // 4px gap between images
  const availableWidth = containerWidth - gaps;
  
  return Math.round(availableWidth / sumOfAspectRatios);
}

export const AdaptiveMasonryLayout: React.FC<AdaptiveMasonryLayoutProps> = ({
  items,
  className = '',
  onItemClick,
  maxItems,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(400);
  
  // Measure container width
  React.useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  
  // Limit items if maxItems is specified
  const displayItems = maxItems ? items.slice(0, maxItems) : items;
  const hasMoreItems = maxItems && items.length > maxItems;
  
  // Calculate layout
  const targetWeight = getTargetWeight(containerWidth);
  const rows = calculateRows(displayItems, targetWeight);
  
  if (displayItems.length === 0) {
    return null;
  }
  
  return (
    <div ref={containerRef} className={`space-y-1 ${className}`}>
      {rows.map((row, rowIndex) => {
        const rowHeight = calculateRowHeight(row, containerWidth);
        
        return (
          <div key={rowIndex} className="flex gap-1">
            {row.items.map((item, itemIndex) => {
              const aspectRatio = item.width && item.height ? item.width / item.height : 1;
              const width = Math.round(rowHeight * aspectRatio);
              const globalIndex = rows.slice(0, rowIndex).reduce((sum, r) => sum + r.items.length, 0) + itemIndex;
              
              return (
                <div
                  key={item.id}
                  className={`relative overflow-hidden rounded ${
                    onItemClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''
                  }`}
                  style={{
                    width: `${width}px`,
                    height: `${rowHeight}px`,
                    flexShrink: 0,
                  }}
                  onClick={() => onItemClick?.(item, globalIndex)}
                >
                  {item.type === 'image' ? (
                    <img
                      src={item.url}
                      alt={item.altText || `Image ${globalIndex + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <video
                      src={item.url}
                      className="w-full h-full object-cover"
                      preload="metadata"
                      muted
                    />
                  )}
                  
                  {/* More items indicator */}
                  {hasMoreItems && globalIndex === displayItems.length - 1 && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <span className="text-white text-lg font-semibold">
                        +{items.length - maxItems!}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};